/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Feature, GeoJsonProperties, Geometry } from 'geojson';
import type {
  Coverage,
  CoverageCollection,
  PointDomain,
  PointSeriesDomain,
} from '@opengeoweb/shared';

export const coverageCollectionToFeatureCollection = (
  cov:
    | CoverageCollection<PointDomain | PointSeriesDomain>
    | Coverage<PointDomain | PointSeriesDomain>
    | undefined,
  parameterNames: string,
): GeoJSON.FeatureCollection => {
  type LatLonType = Record<string, Record<string, string | number | undefined>>;

  if (cov === undefined) {
    return {
      features: [],
      type: 'FeatureCollection',
    };
  }

  // For coverage collection
  if (cov && typeof cov === 'object' && 'coverages' in cov) {
    const latLonValues =
      cov?.coverages?.reduce<LatLonType>(
        (coordinateObj: LatLonType, coverage) => {
          // Extract all parameter from coverage
          const paramVals: Record<string, string | number | undefined> = {};
          // Add parameters values to latlon position
          const latlon = `${coverage.domain.axes.x.values[0]},${coverage.domain.axes.y.values[0]}`;
          if (coordinateObj[latlon] === undefined) {
            parameterNames.split(',').forEach((parameterName) => {
              paramVals[parameterName] = coverage.ranges[parameterName]
                ? coverage.ranges[parameterName]?.values[0]
                : undefined;
            });
            return {
              ...coordinateObj,
              [latlon]: paramVals,
            };
          }
          // if it already exists, don't overwrite existing values unless they are undefined
          const currentVals = coordinateObj[latlon];
          parameterNames.split(',').forEach((parameterName) => {
            if (
              coverage.ranges[parameterName] &&
              currentVals[parameterName] === undefined
            ) {
              paramVals[parameterName] =
                coverage.ranges[parameterName]?.values[0];
            }
          });
          return {
            ...coordinateObj,
            [latlon]: { ...coordinateObj[latlon], ...paramVals },
          };
        },
        {},
      ) ?? {};

    const features: Feature<Geometry, GeoJsonProperties>[] = Object.keys(
      latLonValues,
    ).map((latlon) => {
      const latlonvalues = latlon.split(',');
      return {
        type: 'Feature',
        properties: { values: { ...latLonValues[latlon] } },
        geometry: {
          coordinates: [Number(latlonvalues[0]), Number(latlonvalues[1])],
          type: 'Point',
        },
      };
    });

    const featurePoint: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features,
    };
    return featurePoint;
  }

  // For Coverage

  const paramVals: Record<string, string | number | undefined> = {};
  parameterNames.split(',').forEach((parameterName) => {
    paramVals[parameterName] = cov?.ranges[parameterName]
      ? cov.ranges[parameterName]?.values[0]
      : undefined;
  });

  const featurePoint: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: { values: { ...paramVals } },
        geometry: {
          coordinates: [
            Number(cov?.domain.axes.x.values[0]),
            Number(cov?.domain.axes.y.values[0]),
          ],
          type: 'Point',
        },
      },
    ],
  };
  return featurePoint;
};
